import React, { useState, useEffect } from "react";
import { useHistory } from "react-router";
import { VehicleData, ResponseRecords, VINResponse } from "../AddVehicleByLicensePlateVin/AddVehicleTypes";
import { generatePath } from "../../../utilities/RedirectUtils";
import {
  SelectablePanel,
  Divider,
  Flex,
  Grid,
  Headline,
  Icon,
  Type,
  Utility,
  Dropdown,
  Button,
  FormOptionGroup,
} from "mesh-component-library";
import { CloseIcon } from "mesh-icon-library";
import { useLocale } from "../../../contexts/LocaleContext";
import { TRANSLATION_CONSTANTS } from "../../../app-consts/translations";
import { getTranslatedColors } from "../../../utils/translatedTextUtils";

import Spinner from "../../../components/spinner";
import { FUEL_SERVICE_ID, GAS, VEHICLE_NOT_FOUND } from "../../../app-consts/appConstants";
import { LicensePlateVinLookupMethods } from "../../../reducers/reducer-license-plate-vin-lookup";
import isEmpty from "lodash/isEmpty";
import { FuelAlert } from "../../../components";

export type VehicleSearchResultsProps = {
  searchResults?: ResponseRecords[];
  isLoading: boolean;
  serviceType: number;
  serviceRequestPayload: any;
  commonBenefits: any;
  vinSearchResult: VINResponse;
  changeLookupTypeMethod: (LookupType: LicensePlateVinLookupMethods) => void;
  resetMakeModel: () => void;
  updateVehicleInfo: (data: any) => void;
  selectVehicle: (data: VehicleData) => void;
  selectVehicleColor: (color: string) => void;
  selectVehicleFuelType: (fuelType: any) => void;
  addVehicle: () => void;
  resetSearchResults: () => void;
  method: string;
  licensePlateFailedCount: number;
  updateServiceTransition: (payload: { declineType: string }) => void;
  vinFailedCount: number;
};

export const VehicleSearchResults = ({
  addVehicle,
  commonBenefits,
  changeLookupTypeMethod,
  isLoading,
  licensePlateFailedCount,
  method,
  resetMakeModel,
  resetSearchResults,
  searchResults,
  selectVehicle,
  selectVehicleColor,
  selectVehicleFuelType,
  serviceType,
  serviceRequestPayload,
  updateServiceTransition,
  updateVehicleInfo,
  vinFailedCount,
  vinSearchResult,
}: VehicleSearchResultsProps) => {
  const [selectedVehicle, setSelectedVehicle] = useState(undefined);
  const [selectedColor, setColorSelected] = useState("");
  const [continueButtonDisabled, setContinueButtonDisabled] = useState(true);
  const isFuelDelivery = serviceType === FUEL_SERVICE_ID;
  const [fuelType, setFuelType] = useState(serviceRequestPayload?.serviceRequest.serviceTypeOptions?.gas || "");
  const getTranslatedText = useLocale();
  const history = useHistory();
  const handleCloseClick = () => {
    history.push(generatePath("location"));
  };

  let vehicles;
  if (searchResults && Array.isArray(searchResults) && searchResults.length > 0) {
    if (searchResults[0]?.vehicles) {
      vehicles = searchResults[0].vehicles;
    } else {
      vehicles = searchResults;
    }
  } else if (vinSearchResult && Array.isArray(vinSearchResult) && vinSearchResult.length > 0) {
    if (vinSearchResult[0]?.vehicles) {
      vehicles = vinSearchResult[0].vehicles;
    } else {
      vehicles = vinSearchResult;
    }
  }

  useEffect(() => {
    const canContinue = selectedVehicle && selectedColor && (!isFuelDelivery || (isFuelDelivery && fuelType));
    setContinueButtonDisabled(!canContinue);
  }, [selectedVehicle, selectedColor, fuelType, isFuelDelivery]);

  useEffect(() => {
    if (licensePlateFailedCount > 1 && vinFailedCount > 1) {
      updateServiceTransition({
        declineType: VEHICLE_NOT_FOUND,
      });
      history.push(generatePath("ServiceTransition"));
    } else if (searchResults === null && method === "License Plate") {
      changeLookupTypeMethod("VIN");
      history.push(generatePath("add-vehicle"));
    } else if (vinSearchResult?.error && method === "VIN") {
      changeLookupTypeMethod("License Plate");
      history.push(generatePath("add-vehicle"));
    }
  }, [searchResults, vinSearchResult?.error, licensePlateFailedCount, vinFailedCount]);

  const vehicleOptionSelected = (event, vehicle) => {
    setSelectedVehicle(vehicle);
    selectVehicle(vehicle);
  };

  const vehicleColorSelected = ({ target: { value } }) => {
    if (value === getTranslatedText(TRANSLATION_CONSTANTS.SELECT_ONE)) {
      setColorSelected("");
      selectVehicleColor("");
      setContinueButtonDisabled(true);
      return;
    }
    setColorSelected(value);
    selectVehicleColor(value);
  };

  const selectFuelType = (event) => {
    setFuelType(event.currentTarget.value);
    selectVehicleFuelType(event.currentTarget.value);
  };

  const searchByVinInsteadSelected = () => {
    changeLookupTypeMethod("VIN");
    history.push(generatePath("add-vehicle"));
    resetSearchResults();
  };

  const handleAddVehicle = () => {
    resetMakeModel();
    updateVehicleInfo({ addedVehicleFlag: true });
    updateVehicleInfo({ selectedVehicleIndex: commonBenefits.vehicles.length });
    addVehicle();
    history.push(generatePath("location"));
  };

  const determineFuelAmount = () => {
    const { experience, services } = commonBenefits;
    const quantity =
      experience?.supportedRegions[0] === "CA"
        ? services?.filter((service) => service.type === "Gas")[0].fuelQtyCan
        : services?.filter((service) => service.type === "Gas")[0].fuelQty;
    return quantity <= 0 ? 2 : quantity;
  };

  const fuelServiceCoverages = commonBenefits?.services?.find((service) => service.type === GAS)?.coverages;
  const fuelCostCovered = commonBenefits?.experience?.fuelCostCovered;
  const unitOfMeasure = commonBenefits?.experience?.unitOfMeasure;

  return (
    <Utility.Div data-dd-privacy="allow">
      {isLoading && <Spinner />}
      <Grid
        id="addVehicleScreen"
        utils={{
          hrLeft: "md",
          hrRight: "md",
          vrTop: "md",
        }}
        verticalAlign="middle"
      >
        <Grid.Column col={{ sm: 12, md: 4, lg: 4, xl: 4 }} offset={{ md: 4, lg: 4, xl: 4 }}>
          {/* Start of "Header" portion */}
          <Grid.Column>
            <Flex alignItems="stretch" justifyContent="center" isBlock={true}>
              <Headline
                title={
                  <Type
                    variant="brand"
                    align="left"
                    size="3xl"
                    weight="semibold"
                    lineHeight="md"
                    data-testid="add-vehicle-title"
                  >
                    {getTranslatedText(TRANSLATION_CONSTANTS.VEHICLE_INFO)}
                  </Type>
                }
              />
              <Utility.Div onClick={handleCloseClick} data-testid="add-vehicle-close-icon">
                <Icon icon={CloseIcon} size={1.75} />
              </Utility.Div>
            </Flex>
          </Grid.Column>
          <Divider thickness="thick" utils={{ vrBottom: "md" }} />
          {/* End of "Header" portion */}
          {/* Display vehicle selection components based on partner requirements */}
          {vehicles && vehicles.length > 0 && (
            <Grid
              utils={{
                vrTop: "sm",
              }}
            >
              <Grid.Column>
                <Type
                  element="h1"
                  weight="semibold"
                  align="left"
                  size="lg"
                  lineHeight="md"
                  utils={{
                    vrTop: "md",
                  }}
                >
                  {getTranslatedText(TRANSLATION_CONSTANTS.PLEASE_CONFIRM_VEHICLE)}
                </Type>
              </Grid.Column>
              <Grid.Column>
                <Type element="h1" weight="semibold" align="left" size="lg" lineHeight="md">
                  {`${getTranslatedText(TRANSLATION_CONSTANTS.FOUND)} ${vehicles?.length} ${getTranslatedText(
                    TRANSLATION_CONSTANTS.VEHICLES
                  )}`}
                </Type>
              </Grid.Column>
            </Grid>
          )}
          {vehicles?.map((vehicle, index) => {
            let yearMakeModel = `${vehicle.modelYear || ""} ${vehicle.makeCode || ""} ${vehicle.modelDesc || ""}`;
            return (
              <SelectablePanel
                key={index}
                id={vehicle.vin}
                checked={selectedVehicle?.vin === vehicle?.vin}
                hasBorder
                showInput
                text={yearMakeModel}
                type="radio"
                hasError={!selectedVehicle}
                errorText={getTranslatedText(TRANSLATION_CONSTANTS.PLEASE_CONFIRM_VEHICLE)}
                data-testid={`add-vehicle-selectable-panel-${index}`}
                onChange={(event) => {
                  vehicleOptionSelected(event, vehicle);
                }}
              />
            );
          })}
        </Grid.Column>
        {selectedVehicle && (
          <Grid.Column>
            <Dropdown
              id="color"
              name="color"
              label={getTranslatedText(TRANSLATION_CONSTANTS.COLOR)}
              value={selectedColor.toUpperCase()}
              defaultOption={{
                text: "",
                value: getTranslatedText(TRANSLATION_CONSTANTS.SELECT_ONE),
              }}
              options={getTranslatedColors(getTranslatedText)}
              onChange={vehicleColorSelected}
              required
              sort="asc"
              data-testid="add-vehicle-color-dropdown"
            />
          </Grid.Column>
        )}

        {serviceType === FUEL_SERVICE_ID && selectedVehicle && (
          <React.Fragment>
            <Grid.Column util={{ vrTop: "md" }}>
              <FormOptionGroup
                data-testid="add-vehicle-fuel-type"
                label={getTranslatedText(TRANSLATION_CONSTANTS.FUEL_TYPE)}
                id="helpmechoose"
                type="radio"
                required={true}
                options={[
                  {
                    id: "helpmechoose1",
                    name: "helpmechoose",
                    text: getTranslatedText(TRANSLATION_CONSTANTS.UNLEADED),
                    value: "Unleaded",
                    defaultChecked: fuelType === "Unleaded",
                    // @ts-ignore
                    "data-testid": "add-vehicle-fuel-unleaded",
                    onChange: (event) => {
                      selectFuelType(event);
                    },
                  },
                  {
                    id: "helpmechoose2",
                    name: "helpmechoose",
                    text: getTranslatedText(TRANSLATION_CONSTANTS.DIESEL),
                    value: "Diesel",
                    defaultChecked: fuelType === "Diesel",
                    // @ts-ignore
                    "data-testid": "add-vehicle-fuel-diesel",
                    onChange: (event) => {
                      selectFuelType(event);
                    },
                  },
                ]}
              />
            </Grid.Column>
            {commonBenefits.experience?.fuelCostCovered && !isEmpty(fuelServiceCoverages) && (
              <Grid.Column>
                <FuelAlert fuelServiceCoverages={fuelServiceCoverages} />
              </Grid.Column>
            )}
            <Grid.Column>
              <Divider thickness="thick" />
            </Grid.Column>
          </React.Fragment>
        )}
        {serviceType === FUEL_SERVICE_ID && selectedVehicle && !fuelCostCovered && (
          <Grid.Column>
            <Type variant="dark">
              {`${getTranslatedText(
                TRANSLATION_CONSTANTS.DELIVER_UP_TO
              )} ${determineFuelAmount()} ${unitOfMeasure}. ${getTranslatedText(
                TRANSLATION_CONSTANTS.COST_FUEL_NOT_INCLUDED
              )}`}
            </Type>
          </Grid.Column>
        )}
        <Grid.Column>
          <Button
            size="lg"
            hasUpgrade
            id="vehicle-selection-continue"
            data-testid="add-vehicle-continue-button"
            disabled={continueButtonDisabled}
            onClick={handleAddVehicle}
            type="submit"
            utils={{
              fullWidth: true,
            }}
          >
            {getTranslatedText(TRANSLATION_CONSTANTS.CONTINUE)}
          </Button>
        </Grid.Column>
        <Grid.Column>
          <Type element="span" size="4xl" variant="darker" weight="normal">
            - {getTranslatedText(TRANSLATION_CONSTANTS.OR)} -
          </Type>
        </Grid.Column>
        <Grid.Column>
          <Type element="span" size="lg" variant="information" weight="semibold" onClick={searchByVinInsteadSelected}>
            {getTranslatedText(TRANSLATION_CONSTANTS.SEARCH_BY_VIN)}
          </Type>
        </Grid.Column>
      </Grid>
    </Utility.Div>
  );
};
